/**
 * Analytics Dashboard - Main analytics and insights dashboard
 *
 * Wired to real API data via existing hooks:
 *   - useAdminDashboardStats() -- aggregate counts (org-wide; admin-only)
 *   - useObservations()        -- observation list data
 *   - useAssessments()         -- assessment list data
 *   - useChildren()            -- children list data
 *   - useClassroomsWithStats() -- classroom-level stats
 *
 * This is a school-wide view, so we use the admin endpoint. The route
 * is gated to ``is_staff`` users at the sidebar level today; if a plain
 * teacher reaches the URL directly, the admin-stats fetch will 403 and
 * the page will surface that as an error -- which is correct.
 */

import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  AcademicCapIcon,
  UserGroupIcon,
  ClockIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import { H1, H3, H4, Body, Card, Badge } from '../components/ui';
import { Spinner } from '../components/ui/Loading';
import { BarChart, PieChart } from '../components/charts';
import { useAdminDashboardStats } from '../hooks/api/useDashboard';
import { useObservations } from '../hooks/api/useObservations';
import { useAssessments } from '../hooks/api/useAssessments';
import { useChildren } from '../hooks/api/useChildren';
import { useClassroomsWithStats } from '../hooks/api/useClassrooms';
import { formatDate } from '../utils/date';

type ActiveTab = 'overview' | 'classroom-insights';

const AnalyticsDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview');
  const [_periodFilter, setPeriodFilter] = useState<string>('day');

  // ── Real API Data ──────────────────────────────────────────────────────────
  const {
    data: dashboardStats,
    isLoading: isLoadingStats,
    error: statsError,
  } = useAdminDashboardStats();

  const {
    data: observationsData,
    isLoading: isLoadingObs,
  } = useObservations({ limit: 200 });

  const {
    data: assessmentsData,
    isLoading: isLoadingAssess,
  } = useAssessments({ limit: 200 });

  const {
    data: childrenData,
    isLoading: isLoadingChildren,
  } = useChildren({ limit: 200 });

  const {
    data: classroomsWithStats,
    isLoading: isLoadingClassrooms,
  } = useClassroomsWithStats();

  // ── Derived Analytics ──────────────────────────────────────────────────────

  const observations = observationsData?.items ?? [];
  const assessments = assessmentsData?.items ?? [];
  const children = childrenData?.items ?? [];
  const classrooms = classroomsWithStats ?? [];

  // Observation category distribution for pie chart
  const categoryDistribution = useMemo(() => {
    const counts: Record<string, number> = {
      general: 0,
      academic: 0,
      social: 0,
      emotional: 0,
      physical: 0,
    };
    for (const obs of observations) {
      const cat = obs.category ?? 'general';
      counts[cat] = (counts[cat] ?? 0) + 1;
    }
    const colorMap: Record<string, string> = {
      general: '#6b7280',
      academic: '#3b82f6',
      social: '#10b981',
      emotional: '#f59e0b',
      physical: '#ef4444',
    };
    return Object.entries(counts)
      .filter(([, count]) => count > 0)
      .map(([cat, count]) => ({
        x: cat.charAt(0).toUpperCase() + cat.slice(1),
        y: count,
        label: cat.charAt(0).toUpperCase() + cat.slice(1),
        color: colorMap[cat] ?? '#6b7280',
      }));
  }, [observations]);

  // Classroom performance bar chart data
  const classroomBarData = useMemo(() => {
    return classrooms.map((cr) => ({
      x: cr.name,
      y: cr.stats.total_observations + cr.stats.total_assessments,
      label: cr.name,
    }));
  }, [classrooms]);

  // Assessment score distribution (bar chart)
  const scoreDistribution = useMemo(() => {
    const buckets: Record<string, number> = {};
    for (const a of assessments) {
      const score = a.score ?? 0;
      const key = `Score ${score}`;
      buckets[key] = (buckets[key] ?? 0) + 1;
    }
    return Object.entries(buckets)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([label, count]) => ({ x: label, y: count, label }));
  }, [assessments]);

  // Observation insights
  const observationInsights = useMemo(() => {
    const milestones = observations.filter((o) => o.is_milestone).length;
    const concerns = observations.filter((o) => o.is_concern).length;
    const followUps = observations.filter((o) => o.follow_up_required).length;
    const published = observations.filter((o) => o.is_published).length;
    return { milestones, concerns, followUps, published };
  }, [observations]);

  // ── Loading / Error States ─────────────────────────────────────────────────

  const isLoading = isLoadingStats || isLoadingObs || isLoadingAssess || isLoadingChildren || isLoadingClassrooms;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="analytics-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  if (statsError) {
    return (
      <Card className="p-6 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 m-6">
        <div className="text-red-600 dark:text-red-400">
          Error loading analytics dashboard
        </div>
      </Card>
    );
  }

  const stats = dashboardStats!;

  const tabs: Array<{ id: ActiveTab; label: string; icon: React.FC<React.SVGProps<SVGSVGElement>> }> = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon },
    { id: 'classroom-insights', label: 'Classroom Insights', icon: UserGroupIcon },
  ];

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between"
      >
        <div className="flex items-center space-x-3">
          <ChartBarIcon className="w-8 h-8 text-blue-500" />
          <H1>Analytics Dashboard</H1>
        </div>

        <div className="flex items-center space-x-3">
          {/* Period Filter */}
          <div className="flex items-center space-x-2">
            <CalendarIcon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
            <select
              value={_periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            >
              <option value="day">Daily</option>
              <option value="week">Weekly</option>
              <option value="month">Monthly</option>
              <option value="quarter">Quarterly</option>
            </select>
          </div>
        </div>
      </motion.div>

      {/* Key Metrics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4"
        data-testid="stats-cards"
      >
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <Body className="text-sm text-gray-600 dark:text-gray-400">Total Children</Body>
              <H3>{stats.total_children.toLocaleString()}</H3>
            </div>
            <UserGroupIcon className="w-6 h-6 text-blue-500" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <Body className="text-sm text-gray-600 dark:text-gray-400">Teachers</Body>
              <H3>{stats.total_teachers}</H3>
            </div>
            <AcademicCapIcon className="w-6 h-6 text-green-500" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <Body className="text-sm text-gray-600 dark:text-gray-400">Classrooms</Body>
              <H3>{stats.total_classrooms}</H3>
            </div>
            <UserGroupIcon className="w-6 h-6 text-purple-500" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <Body className="text-sm text-gray-600 dark:text-gray-400">Observations</Body>
              <H3>{stats.total_observations.toLocaleString()}</H3>
            </div>
            <ClockIcon className="w-6 h-6 text-orange-500" />
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <Body className="text-sm text-gray-600 dark:text-gray-400">Assessments</Body>
              <H3>{stats.total_assessments.toLocaleString()}</H3>
            </div>
            <ChartBarIcon className="w-6 h-6 text-pink-500" />
          </div>
        </Card>
      </motion.div>

      {/* Navigation Tabs */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="border-b border-gray-200 dark:border-gray-700"
      >
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </motion.div>

      {/* Tab Content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Observation Category Distribution + Assessment Score Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="p-6">
                <H3 className="mb-4">Observation Categories</H3>
                {categoryDistribution.length > 0 ? (
                  <PieChart
                    data={categoryDistribution}
                    height={300}
                    showPercentages={true}
                    showLabels={true}
                  />
                ) : (
                  <Body className="text-gray-500 dark:text-gray-400">No observations recorded yet.</Body>
                )}
              </Card>

              <Card className="p-6">
                <H3 className="mb-4">Assessment Score Distribution</H3>
                {scoreDistribution.length > 0 ? (
                  <BarChart
                    data={scoreDistribution}
                    height={300}
                    showValues={true}
                    valuePosition="top"
                    gradientFill={true}
                    margins={{ top: 30, right: 20, bottom: 60, left: 60 }}
                  />
                ) : (
                  <Body className="text-gray-500 dark:text-gray-400">No assessments recorded yet.</Body>
                )}
              </Card>
            </div>

            {/* Observation Insights */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="p-4 bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                <H4 className="text-green-800 dark:text-green-200 mb-2">Milestones</H4>
                <Body className="text-green-700 dark:text-green-300 text-sm mb-2">
                  Milestone observations recorded
                </Body>
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                  {observationInsights.milestones}
                </div>
              </Card>

              <Card className="p-4 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
                <H4 className="text-red-800 dark:text-red-200 mb-2">Concerns</H4>
                <Body className="text-red-700 dark:text-red-300 text-sm mb-2">
                  Concern observations raised
                </Body>
                <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                  {observationInsights.concerns}
                </div>
              </Card>

              <Card className="p-4 bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800">
                <H4 className="text-orange-800 dark:text-orange-200 mb-2">Follow-ups</H4>
                <Body className="text-orange-700 dark:text-orange-300 text-sm mb-2">
                  Observations needing follow-up
                </Body>
                <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {observationInsights.followUps}
                </div>
              </Card>

              <Card className="p-4 bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800">
                <H4 className="text-blue-800 dark:text-blue-200 mb-2">This Week</H4>
                <Body className="text-blue-700 dark:text-blue-300 text-sm mb-2">
                  Observations recorded this week
                </Body>
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {stats.observations_this_week}
                </div>
              </Card>
            </div>

            {/* Recent Activity Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Recent Observations */}
              <Card className="p-6">
                <H3 className="mb-4">Recent Observations</H3>
                {stats.recent_observations.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_observations.map((obs) => (
                      <div
                        key={obs.id}
                        className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <ClockIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                            {obs.child_name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {obs.category && (
                              <Badge variant="secondary" size="sm">
                                {obs.category}
                              </Badge>
                            )}{' '}
                            {formatDate(obs.observation_date)} &middot; {obs.teacher_name}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">
                            {obs.narrative}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Body className="text-gray-500 dark:text-gray-400">No recent observations.</Body>
                )}
              </Card>

              {/* Recent Assessments */}
              <Card className="p-6">
                <H3 className="mb-4">Recent Assessments</H3>
                {stats.recent_assessments.length > 0 ? (
                  <div className="space-y-3">
                    {stats.recent_assessments.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                      >
                        <ChartBarIcon className="w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                            {a.child_name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {a.learning_area_name && (
                              <Badge variant="secondary" size="sm">
                                {a.learning_area_name}
                              </Badge>
                            )}{' '}
                            {formatDate(a.assessment_date)}
                          </div>
                          <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                            Score: <span className="font-semibold">{a.score}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Body className="text-gray-500 dark:text-gray-400">No recent assessments.</Body>
                )}
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'classroom-insights' && (
          <div className="space-y-6">
            {/* Classroom Activity Bar Chart */}
            <Card className="p-6">
              <H3 className="mb-4">Classroom Activity (Observations + Assessments)</H3>
              {classroomBarData.length > 0 ? (
                <BarChart
                  data={classroomBarData}
                  height={300}
                  showValues={true}
                  valuePosition="top"
                  gradientFill={true}
                  margins={{ top: 30, right: 20, bottom: 80, left: 60 }}
                />
              ) : (
                <Body className="text-gray-500 dark:text-gray-400">No classroom data available.</Body>
              )}
            </Card>

            {/* Classroom Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {classrooms.map((cr) => (
                <Card key={cr.id} className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <H4 className="truncate">{cr.name}</H4>
                    {cr.grade_name && (
                      <Badge variant="secondary" size="sm">
                        {cr.grade_name}
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <Body className="text-sm text-gray-600 dark:text-gray-400">Children</Body>
                      <Body className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {cr.stats.total_children}
                      </Body>
                    </div>
                    <div className="flex justify-between">
                      <Body className="text-sm text-gray-600 dark:text-gray-400">Active</Body>
                      <Body className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {cr.stats.active_children}
                      </Body>
                    </div>
                    <div className="flex justify-between">
                      <Body className="text-sm text-gray-600 dark:text-gray-400">Observations</Body>
                      <Body className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {cr.stats.total_observations}
                      </Body>
                    </div>
                    <div className="flex justify-between">
                      <Body className="text-sm text-gray-600 dark:text-gray-400">Assessments</Body>
                      <Body className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {cr.stats.total_assessments}
                      </Body>
                    </div>
                  </div>
                </Card>
              ))}
              {classrooms.length === 0 && (
                <Card className="p-6 col-span-full">
                  <Body className="text-gray-500 dark:text-gray-400">No classrooms found.</Body>
                </Card>
              )}
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default AnalyticsDashboard;
